import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Swords, Shield, Zap, Star, Trophy, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/auth';
import type { Card } from '../../types/card';
import { motion, AnimatePresence } from 'framer-motion';

interface CombatSession {
  id: string;
  host_id: string;
  guest_id: string;
  status: 'WAITING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  winner_id: string | null;
  host_cards: Card[];
  guest_cards: Card[];
}

interface CombatRound {
  round_number: number;
  host_card: Card;
  guest_card: Card;
  winner_id: string | null;
}

export default function CombatArena() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [session, setSession] = useState<CombatSession | null>(null);
  const [rounds, setRounds] = useState<CombatRound[]>([]);
  const [currentRound, setCurrentRound] = useState(0);
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sessionId || !user) return;

    const fetchSession = async () => {
      try {
        const { data, error } = await supabase
          .from('combat_sessions')
          .select('*')
          .eq('id', sessionId)
          .single();

        if (error) throw error;
        setSession(data);

        // Subscribe to session changes
        const subscription = supabase
          .channel(`combat_${sessionId}`)
          .on('postgres_changes', { 
            event: '*', 
            schema: 'public', 
            table: 'combat_sessions',
            filter: `id=eq.${sessionId}`
          }, payload => {
            setSession(payload.new as CombatSession);
          })
          .subscribe();

        return () => {
          subscription.unsubscribe();
        };
      } catch (err) {
        console.error('Error fetching session:', err);
        setError('Erreur lors du chargement du combat');
      } finally {
        setLoading(false);
      }
    };

    fetchSession();
  }, [sessionId, user]);

  const calculateWinner = (hostCard: Card, guestCard: Card) => {
    const hostScore = hostCard.attaque + hostCard.defense + hostCard.special;
    const guestScore = guestCard.attaque + guestCard.defense + guestCard.special;
    return hostScore > guestScore ? session?.host_id : 
           guestScore > hostScore ? session?.guest_id : 
           null;
  };

  const playCard = async (card: Card) => {
    if (!session || !user) return;

    try {
      const isHost = user.id === session.host_id;
      const round = currentRound + 1;

      // Create or update round
      const { error: roundError } = await supabase
        .from('combat_rounds')
        .upsert({
          session_id: session.id,
          round_number: round,
          [isHost ? 'host_card' : 'guest_card']: card
        });

      if (roundError) throw roundError;

      // Get complete round data
      const { data: roundData, error: fetchError } = await supabase
        .from('combat_rounds')
        .select('*')
        .eq('session_id', session.id)
        .eq('round_number', round)
        .single();

      if (fetchError) throw fetchError;

      if (roundData.host_card && roundData.guest_card) {
        const winner = calculateWinner(roundData.host_card, roundData.guest_card);
        
        // Update round with winner
        await supabase
          .from('combat_rounds')
          .update({ winner_id: winner })
          .eq('id', roundData.id);

        // Check if combat is complete
        const { data: allRounds } = await supabase
          .from('combat_rounds')
          .select('winner_id')
          .eq('session_id', session.id);

        const hostWins = allRounds?.filter(r => r.winner_id === session.host_id).length || 0;
        const guestWins = allRounds?.filter(r => r.winner_id === session.guest_id).length || 0;

        if (hostWins === 2 || guestWins === 2) {
          const winner = hostWins === 2 ? session.host_id : session.guest_id;
          
          // Update session
          await supabase
            .from('combat_sessions')
            .update({
              status: 'COMPLETED',
              winner_id: winner
            })
            .eq('id', session.id);

          // Update combat stats
          const players = [session.host_id, session.guest_id];
          for (const playerId of players) {
            const isWinner = playerId === winner;
            await supabase.rpc('update_combat_stats', {
              player_id: playerId,
              won: isWinner
            });
          }
        }
      }
    } catch (err) {
      console.error('Error playing card:', err);
      setError('Erreur lors du jeu de la carte');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <div className="flex items-center text-red-500 mb-4">
            <X className="h-6 w-6 mr-2" />
            <h2 className="text-xl font-bold">Erreur</h2>
          </div>
          <p className="text-gray-600">{error || 'Session de combat introuvable'}</p>
          <button
            onClick={() => navigate('/combat')}
            className="mt-4 w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
          >
            Retour aux combats
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900">Combat en cours</h1>
            {session.status === 'COMPLETED' && (
              <div className="flex items-center text-yellow-500">
                <Trophy className="h-6 w-6 mr-2" />
                <span className="font-medium">
                  {session.winner_id === user?.id ? 'Victoire !' : 'Défaite'}
                </span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Zone de jeu */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h2 className="text-lg font-semibold mb-4">Zone de combat</h2>
              <AnimatePresence>
                {rounds[currentRound] && (
                  <div className="grid grid-cols-2 gap-4">
                    <motion.div
                      initial={{ opacity: 0, x: -50 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -50 }}
                      className="bg-white p-4 rounded-lg shadow-sm"
                    >
                      {/* Carte du joueur 1 */}
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 50 }}
                      className="bg-white p-4 rounded-lg shadow-sm"
                    >
                      {/* Carte du joueur 2 */}
                    </motion.div>
                  </div>
                )}
              </AnimatePresence>
            </div>

            {/* Cartes du joueur */}
            <div>
              <h2 className="text-lg font-semibold mb-4">Vos cartes</h2>
              <div className="grid grid-cols-1 gap-4">
                {(user?.id === session.host_id ? session.host_cards : session.guest_cards)
                  ?.filter(card => !rounds.some(r => 
                    (user?.id === session.host_id ? r.host_card : r.guest_card)?.id === card.id
                  ))
                  .map(card => (
                    <div
                      key={card.id}
                      onClick={() => session.status === 'IN_PROGRESS' && playCard(card)}
                      className={`bg-white p-4 rounded-lg shadow-sm cursor-pointer hover:shadow-md transition-shadow ${
                        selectedCard?.id === card.id ? 'ring-2 ring-indigo-500' : ''
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <h3 className="font-medium text-gray-900">{card.surnom}</h3>
                        <div className="flex items-center bg-gray-100 px-2 py-1 rounded">
                          <Star className="w-4 h-4 text-yellow-500 mr-1" />
                          <span className="text-sm font-medium">
                            {card.attaque + card.defense + card.special}
                          </span>
                        </div>
                      </div>
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center text-sm">
                          <Swords className="w-4 h-4 mr-2 text-red-500" />
                          <span>{card.attaque}</span>
                        </div>
                        <div className="flex items-center text-sm">
                          <Shield className="w-4 h-4 mr-2 text-blue-500" />
                          <span>{card.defense}</span>
                        </div>
                        <div className="flex items-center text-sm">
                          <Zap className="w-4 h-4 mr-2 text-yellow-500" />
                          <span>{card.special}</span>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}